import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { marked } from 'marked';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../utils/api';
import './LessonPage.css';

// Configure marked
marked.setOptions({ breaks: true, gfm: true });

// ── Extracted outside LessonPage to prevent remount on every render ──────────
function TutorContent({
  onClose,
  curMod,
  lessonIdx,
  messages,
  msgsRef,
  tutorLoading,
  user,
  tutorInput,
  setTutorInput,
  sendTutor,
}) {
  return (
    <>
      <div className="tutor-head">
        {/* Close button — mobile drawer only */}
        <button className="tutor-close" onClick={onClose} aria-label="Close">✕</button>
        <div className="tutor-av">👩‍🏫</div>
        <div>
          <div className="tutor-name">Amanda AI</div>
          <div className="tutor-status">Online — here to help</div>
        </div>
      </div>
      <div className="tutor-ctx">
        📍 {curMod?.title} · Lesson {lessonIdx + 1}
      </div>
      <div className="tutor-msgs" ref={msgsRef}>
        {messages.map((m, i) => (
          <div key={i} className={`tutor-msg ${m.role === 'user' ? 'user' : ''}`}>
            <div className={`tutor-msg-av ${m.role === 'user' ? 'av-user' : 'av-amanda'}`}>
              {m.role === 'user' ? user?.first_name?.[0] || 'Y' : '👩‍🏫'}
            </div>
            <div>
              {m.role === 'assistant' ? (
                <div
                  className="tutor-bubble tutor-bubble--md"
                  dangerouslySetInnerHTML={{ __html: marked.parse(m.content) }}
                />
              ) : (
                <div className="tutor-bubble">{m.content}</div>
              )}
              <div className="tutor-time">Just now</div>
            </div>
          </div>
        ))}
        {tutorLoading && (
          <div className="tutor-msg">
            <div className="tutor-msg-av av-amanda">👩‍🏫</div>
            <div className="tutor-typing">
              <span /><span /><span />
            </div>
          </div>
        )}
      </div>
      <div className="tutor-input-row">
        <input
          className="tutor-input"
          value={tutorInput}
          onChange={e => setTutorInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendTutor()}
          placeholder="Ask Amanda anything…"
          disabled={tutorLoading}
        />
        <button
          className="tutor-send"
          onClick={sendTutor}
          disabled={tutorLoading || !tutorInput.trim()}
        >
          ➤
        </button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function LessonPage() {
  const { slug, moduleId, lessonId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const nav = useNavigate();

  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [curMod, setCurMod] = useState(null);
  const [curLesson, setCurLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  // Mobile tutor drawer
  const [tutorOpen, setTutorOpen] = useState(false);

  // Tutor state
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        "Hi! I'm Amanda AI, your property coach. I'm here throughout every lesson — ask me anything and I'll answer in context of where you are in the course.",
    },
  ]);
  const [tutorInput, setTutorInput] = useState('');
  const [tutorLoading, setTutorLoading] = useState(false);
  const [sessionId] = useState('session-' + Date.now());
  const msgsRef = useRef(null);

  // Resize refs
  const tutorPanelRef = useRef(null);
  const isResizing = useRef(false);

  const onResizeMouseDown = useCallback((e) => {
    isResizing.current = true;
    e.preventDefault();

    const onMouseMove = (e) => {
      if (!isResizing.current || !tutorPanelRef.current) return;
      const panelRect = tutorPanelRef.current.getBoundingClientRect();
      const newWidth = panelRect.right - e.clientX;
      if (newWidth >= 240 && newWidth <= 600) {
        tutorPanelRef.current.style.width = `${newWidth}px`;
      }
    };

    const onMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const completedIds = new Set(progress?.completed_lesson_ids || []);

  const loadCourse = useCallback(async () => {
    try {
      const [cRes, pRes] = await Promise.all([
        api.get(`/courses/${slug}`),
        api.get(`/courses/${slug}/progress`).catch(() => null),
      ]);
      setCourse(cRes.data);
      setProgress(pRes?.data || null);

      const allMods = cRes.data.modules || [];
      const targetMod = moduleId ? allMods.find(m => m.id === moduleId) : allMods[0];
      const targetLesson = lessonId
        ? targetMod?.lessons?.find(l => l.id === lessonId)
        : targetMod?.lessons?.[0];

      setCurMod(targetMod || allMods[0]);
      setCurLesson(targetLesson || allMods[0]?.lessons?.[0]);
    } finally {
      setLoading(false);
    }
  }, [slug, moduleId, lessonId]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [messages, tutorLoading]);

  // Reset tutor greeting when lesson changes
  useEffect(() => {
    if (curLesson) {
      setMessages([
        {
          role: 'assistant',
          content: `We're now on "${curLesson.title}". Ask me anything about this lesson — or anything else property-related.`,
        },
      ]);
    }
  }, [curLesson?.id]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = tutorOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [tutorOpen]);

  const markComplete = async () => {
    if (completing || completedIds.has(curLesson?.id)) return;
    setCompleting(true);
    try {
      const { data } = await api.post(`/courses/${slug}/lessons/${curLesson.id}/complete`);
      if (data.certificate) toast('🏆 Course complete! Your certificate has been issued.', 'success', 5000);
      else toast('✓ Lesson complete!', 'success');
      const pRes = await api.get(`/courses/${slug}/progress`);
      setProgress(pRes.data);
      const modLessons = curMod?.lessons || [];
      const idx = modLessons.findIndex(l => l.id === curLesson.id);
      if (idx < modLessons.length - 1) {
        const next = modLessons[idx + 1];
        nav(`/learn/${slug}/${curMod.id}/${next.id}`);
      } else {
        const allMods = course?.modules || [];
        const mIdx = allMods.findIndex(m => m.id === curMod.id);
        if (mIdx < allMods.length - 1) {
          const nm = allMods[mIdx + 1];
          nav(`/learn/${slug}/${nm.id}/${nm.lessons?.[0]?.id || ''}`);
        }
      }
    } catch {
      toast('Failed to save progress', 'error');
    } finally {
      setCompleting(false);
    }
  };

  const prevLesson = () => {
    const lessons = curMod?.lessons || [];
    const idx = lessons.findIndex(l => l.id === curLesson?.id);
    if (idx > 0) { nav(`/learn/${slug}/${curMod.id}/${lessons[idx - 1].id}`); return; }
    const allMods = course?.modules || [];
    const mIdx = allMods.findIndex(m => m.id === curMod?.id);
    if (mIdx > 0) {
      const pm = allMods[mIdx - 1];
      const pl = pm.lessons?.[pm.lessons.length - 1];
      if (pl) nav(`/learn/${slug}/${pm.id}/${pl.id}`);
    }
  };

  const nextLesson = () => {
    const lessons = curMod?.lessons || [];
    const idx = lessons.findIndex(l => l.id === curLesson?.id);
    if (idx < lessons.length - 1) { nav(`/learn/${slug}/${curMod.id}/${lessons[idx + 1].id}`); return; }
    const allMods = course?.modules || [];
    const mIdx = allMods.findIndex(m => m.id === curMod?.id);
    if (mIdx < allMods.length - 1) {
      const nm = allMods[mIdx + 1];
      nav(`/learn/${slug}/${nm.id}/${nm.lessons?.[0]?.id || ''}`);
    }
  };

  const sendTutor = async () => {
    const msg = tutorInput.trim();
    if (!msg || tutorLoading || !curLesson) return;
    setTutorInput('');
    const history = messages.slice(-8);
    setMessages(m => [...m, { role: 'user', content: msg }]);
    setTutorLoading(true);
    try {
      const { data } = await api.post('/tutor/message', {
        lesson_id: curLesson.id,
        message: msg,
        session_id: sessionId,
        history,
      });
      setMessages(m => [...m, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setMessages(m => [
        ...m,
        {
          role: 'assistant',
          content: err.response?.data?.error || "I'm sorry, I couldn't respond right now. Please try again.",
        },
      ]);
    } finally {
      setTutorLoading(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!course) return <div className="loading-center"><p>Course not found.</p></div>;

  const allMods = course.modules || [];
  const modIdx = allMods.findIndex(m => m.id === curMod?.id);
  const lessonIdx = curMod?.lessons?.findIndex(l => l.id === curLesson?.id) ?? 0;
  const isDone = completedIds.has(curLesson?.id);
  const pct = progress?.percent || 0;
  const isLastLesson =
    modIdx === allMods.length - 1 && lessonIdx === (curMod?.lessons?.length || 1) - 1;

  // Shared props passed to both desktop sidebar and mobile drawer instances
  const tutorProps = {
    onClose: () => setTutorOpen(false),
    curMod,
    lessonIdx,
    messages,
    msgsRef,
    tutorLoading,
    user,
    tutorInput,
    setTutorInput,
    sendTutor,
  };

  return (
    <div className="lesson-page">

      {/* ── LESSON SIDEBAR ──────────────────────────────────────────────── */}
      <aside className="lesson-sb hide-mobile">
        <div className="lesson-sb-head">
          <div className="lesson-sb-course">Course 1 · Module {modIdx + 1}</div>
          <div className="lesson-sb-mod">{curMod?.title}</div>
        </div>
        <div className="lesson-sb-list">
          {curMod?.lessons?.map((l, i) => {
            const done = completedIds.has(l.id);
            const curr = l.id === curLesson?.id;
            return (
              <div
                key={l.id}
                className={`lesson-sb-item ${curr ? 'active' : ''} ${done ? 'done' : ''}`}
                onClick={() => nav(`/learn/${slug}/${curMod.id}/${l.id}`)}
              >
                <div className={`lesson-sb-check ${done ? 'lc-done' : curr ? 'lc-curr' : 'lc-up'}`}>
                  {done ? '✓' : curr ? '▶' : ''}
                </div>
                <span className="lesson-sb-label">{l.title}</span>
                <span className="lesson-sb-dur">{l.duration_mins}m</span>
              </div>
            );
          })}
        </div>
        <div className="lesson-sb-foot">
          <div style={{ marginBottom: '6px', fontSize: '13px', color: 'rgba(255,255,255,.35)' }}>
            All modules
          </div>
          {allMods.map((m, i) => {
            const done = m.lessons?.every(l => completedIds.has(l.id));
            const curr = m.id === curMod?.id;
            return (
              <div
                key={m.id}
                className={`lesson-sb-mod-item ${curr ? 'active' : ''}`}
                onClick={() => nav(`/learn/${slug}/${m.id}/${m.lessons?.[0]?.id || ''}`)}
              >
                <span className={`lesson-sb-mod-dot ${done ? 'dot-done' : curr ? 'dot-curr' : 'dot-lock'}`}>
                  {done ? '✓' : i + 1}
                </span>
                <span>{m.title}</span>
              </div>
            );
          })}
        </div>
      </aside>

      {/* ── LESSON BODY ─────────────────────────────────────────────────── */}
      <div className="lesson-body">
        <div className="lesson-main">

          {/* TOP BAR */}
          <div className="lesson-topbar">
            <div className="lesson-breadcrumb">
              <Link to="/dashboard" className="lesson-back">← Dashboard</Link>
              <span> / Module {modIdx + 1} / </span>
              <strong>{curLesson?.title}</strong>
            </div>
            <div className="lesson-prog">
              <div className="progress-bar" style={{ width: '120px' }}>
                <div className="progress-fill" style={{ width: pct + '%' }} />
              </div>
              <span style={{ fontSize: '13px', color: 'var(--color-text-body)' }}>{pct}%</span>
            </div>
          </div>

          {/* CONTENT */}
          <div className="lesson-content">
            <h1 className="lesson-title">Lesson {lessonIdx + 1} — {curLesson?.title}</h1>

            {/* VIDEO */}
            <div className="lesson-video">
              {curLesson?.video_url ? (
                <iframe
                  src={curLesson.video_url}
                  title={curLesson.title}
                  allowFullScreen
                  style={{ width: '100%', height: '100%', border: 'none', borderRadius: 'var(--r-lg)' }}
                />
              ) : (
                <div className="lesson-video-placeholder">
                  <div className="lesson-video-badge">
                    <span className="lesson-video-dot" />
                    Amanda AI · Your Property Coach
                  </div>
                  <div className="lesson-video-av">👩‍🏫</div>
                  <div className="lesson-video-play">▶ Video coming soon</div>
                </div>
              )}
            </div>

            {/* LESSON TEXT */}
            {curLesson?.content && (
              <div className="lesson-text" dangerouslySetInnerHTML={{ __html: curLesson.content }} />
            )}
            {!curLesson?.content && (
              <div className="lesson-text">
                <p>
                  Lesson content for <strong>{curLesson?.title}</strong> will appear here once
                  uploaded via the admin panel.
                </p>
                <div className="callout mt-2">
                  💡 <strong>Using the AI Tutor:</strong> While this module's video is being
                  produced, Amanda AI in the panel on the right can answer your questions about
                  this topic right now.
                </div>
              </div>
            )}

            {/* DOWNLOADS */}
            {curLesson?.downloads?.length > 0 && (
              <div className="lesson-downloads">
                {curLesson.downloads.map(d => (
                  <a key={d.id} href={d.file_url} target="_blank" rel="noreferrer" className="lesson-dl-pill">
                    📄 {d.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* FOOTER NAV */}
          <div className="lesson-footer">
            <button className="btn btn-ghost btn-sm" onClick={prevLesson}>← Previous</button>
            <button
              className={`btn btn-sm ${isDone ? 'btn-ghost' : 'btn-primary'} ${completing ? 'btn-loading' : ''}`}
              onClick={markComplete}
              disabled={isDone || completing}
            >
              {completing ? '' : isDone ? '✓ Completed' : '✓ Mark as Complete'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={nextLesson}>
              {isLastLesson ? 'Finish ✓' : 'Next →'}
            </button>
          </div>
        </div>

        {/* ── DESKTOP AI TUTOR PANEL ──────────────────────────────────── */}
        <aside className="tutor-panel" ref={tutorPanelRef}>
          <div className="tutor-resize-handle" onMouseDown={onResizeMouseDown} />
          <TutorContent {...tutorProps} />
        </aside>
      </div>

      {/* ── MOBILE: floating button + drawer ──────────────────────────── */}

      {/* Backdrop — tapping closes the drawer */}
      {tutorOpen && (
        <div className="tutor-backdrop" onClick={() => setTutorOpen(false)} />
      )}

      {/* Slide-up drawer */}
      <div className={`tutor-drawer ${tutorOpen ? 'tutor-drawer--open' : ''}`}>
        <TutorContent {...tutorProps} />
      </div>

      {/* Floating "Ask Amanda" button — only shown when drawer is closed */}
      {!tutorOpen && (
        <button className="tutor-fab" onClick={() => setTutorOpen(true)} aria-label="Ask Amanda AI">
          <span className="tutor-fab__av">👩‍🏫</span>
          <span className="tutor-fab__label">Ask Amanda</span>
        </button>
      )}

    </div>
  );
}